import React, { useReducer, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '../../services/api'
import WizardProgress from './WizardProgress'
import BusinessDescriptionStep from './steps/BusinessDescriptionStep'
import TargetAudienceStep from './steps/TargetAudienceStep'
import BrandValuesStep from './steps/BrandValuesStep'
import GeographicContextStep from './steps/GeographicContextStep'
import RestrictionsStep from './steps/RestrictionsStep'
import ProcessingModal from './ProcessingModal'

// Wizard steps configuration
const STEPS = [
  { id: 'business_description', component: BusinessDescriptionStep },
  { id: 'target_audience', component: TargetAudienceStep },
  { id: 'brand_values', component: BrandValuesStep },
  { id: 'geographic_context', component: GeographicContextStep },
  { id: 'content_restrictions', component: RestrictionsStep },
]

// Initial state
const initialState = {
  currentStep: 0,
  isProcessing: false,
  processingError: null,
  completionStatus: {
    business_description: 'pending',
    target_audience: 'pending',
    brand_values: 'pending',
    geographic_context: 'pending',
    content_restrictions: 'pending',
  },
  data: {
    business_description: {
      business_description: '',
    },
    target_audience: {
      age_groups: [],
      interests: [],
      lifestyle: [],
      additional_notes: '',
    },
    brand_values: {
      core_values: [],
      industry: null,
      usps: [],
      usps_other_text: '',
      personality: {
        serious_casual: 3,
        classic_modern: 3,
        reserved_confident: 3,
      },
    },
    geographic_context: {
      business_scope: null,
      primary_location: '',
      target_markets: [],
      cultural_preferences: [],
      cultural_notes: '',
    },
    content_restrictions: {
      avoid_topics: [],
      forbidden_words: [],
      avoid_tones: [],
      additional_restrictions: '',
    },
  },
}

// Action types
const ACTIONS = {
  SET_STEP: 'SET_STEP',
  UPDATE_STEP_DATA: 'UPDATE_STEP_DATA',
  MARK_STEP_COMPLETED: 'MARK_STEP_COMPLETED',
  MARK_STEP_SKIPPED: 'MARK_STEP_SKIPPED',
  START_PROCESSING: 'START_PROCESSING',
  PROCESSING_SUCCESS: 'PROCESSING_SUCCESS',
  PROCESSING_ERROR: 'PROCESSING_ERROR',
  LOAD_EXISTING_DATA: 'LOAD_EXISTING_DATA',
  RESET: 'RESET',
}

// Reducer
function wizardReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_STEP:
      return { ...state, currentStep: action.payload }

    case ACTIONS.UPDATE_STEP_DATA:
      return {
        ...state,
        data: {
          ...state.data,
          [action.payload.stepId]: {
            ...state.data[action.payload.stepId],
            ...action.payload.data,
          },
        },
      }

    case ACTIONS.MARK_STEP_COMPLETED:
      return {
        ...state,
        completionStatus: {
          ...state.completionStatus,
          [action.payload]: 'completed',
        },
      }

    case ACTIONS.MARK_STEP_SKIPPED:
      return {
        ...state,
        completionStatus: {
          ...state.completionStatus,
          [action.payload]: 'skipped',
        },
      }

    case ACTIONS.START_PROCESSING:
      return { ...state, isProcessing: true, processingError: null }

    case ACTIONS.PROCESSING_SUCCESS:
      return { ...state, isProcessing: false }

    case ACTIONS.PROCESSING_ERROR:
      return { ...state, isProcessing: false, processingError: action.payload }

    case ACTIONS.LOAD_EXISTING_DATA:
      return {
        ...state,
        data: action.payload.data || state.data,
        completionStatus: action.payload.completionStatus || state.completionStatus,
      }

    case ACTIONS.RESET:
      return initialState

    default:
      return state
  }
}

/**
 * Brand Profile Wizard - Main Container
 *
 * Guides users through 4 steps to configure their brand profile:
 * 1. Target Audience
 * 2. Brand Values
 * 3. Geographic Context
 * 4. Content Restrictions
 */
export default function BrandProfileWizard({ accountId, onComplete, onCancel, existingProfile }) {
  const { t } = useTranslation('brandProfile')
  const [state, dispatch] = useReducer(wizardReducer, initialState)

  const { currentStep, data, completionStatus, isProcessing, processingError } = state
  const totalSteps = STEPS.length
  const currentStepConfig = STEPS[currentStep]
  const StepComponent = currentStepConfig?.component

  // Load existing profile data if available
  useEffect(() => {
    if (existingProfile?.brand_profile) {
      const profile = existingProfile.brand_profile
      dispatch({
        type: ACTIONS.LOAD_EXISTING_DATA,
        payload: {
          data: {
            business_description: profile.business_description || initialState.data.business_description,
            target_audience: profile.target_audience || initialState.data.target_audience,
            brand_values: profile.brand_values || initialState.data.brand_values,
            geographic_context: profile.geographic_context || initialState.data.geographic_context,
            content_restrictions: profile.content_restrictions || initialState.data.content_restrictions,
          },
          completionStatus: profile.completion_status || initialState.completionStatus,
        },
      })
    }
  }, [existingProfile])

  // Update step data
  const updateStepData = useCallback((stepId, newData) => {
    dispatch({
      type: ACTIONS.UPDATE_STEP_DATA,
      payload: { stepId, data: newData },
    })
  }, [])

  // Submit wizard data (defined first to avoid circular dependency)
  const handleSubmit = useCallback(async () => {
    dispatch({ type: ACTIONS.START_PROCESSING })

    try {
      // Build profile data - only include non-skipped sections
      const profileData = {}

      if (completionStatus.business_description === 'completed') {
        profileData.business_description = data.business_description
      }
      if (completionStatus.target_audience === 'completed') {
        profileData.target_audience = data.target_audience
      }
      if (completionStatus.brand_values === 'completed') {
        profileData.brand_values = data.brand_values
      }
      if (completionStatus.geographic_context === 'completed') {
        profileData.geographic_context = data.geographic_context
      }
      if (completionStatus.content_restrictions === 'completed') {
        profileData.content_restrictions = data.content_restrictions
      }

      // Submit to API
      await api.updateBrandProfile(accountId, profileData)

      // Poll for processing completion
      const status = await api.pollBrandProfileStatus(accountId, 30, 1000)

      if (status.status === 'completed') {
        dispatch({ type: ACTIONS.PROCESSING_SUCCESS })
        onComplete?.(status)
      } else if (status.status === 'error') {
        throw new Error(status.error || t('wizard.processingFailed'))
      } else {
        throw new Error(t('wizard.processingFailed'))
      }
    } catch (error) {
      dispatch({
        type: ACTIONS.PROCESSING_ERROR,
        payload: error.message || t('wizard.genericError'),
      })
    }
  }, [accountId, data, completionStatus, onComplete, t])

  // Navigate to next step
  const handleNext = useCallback(() => {
    // Mark current step as completed
    dispatch({
      type: ACTIONS.MARK_STEP_COMPLETED,
      payload: currentStepConfig.id,
    })

    if (currentStep < totalSteps - 1) {
      dispatch({ type: ACTIONS.SET_STEP, payload: currentStep + 1 })
    } else {
      // Final step - submit the wizard
      handleSubmit()
    }
  }, [currentStep, totalSteps, currentStepConfig, handleSubmit])

  // Skip current step
  const handleSkip = useCallback(() => {
    dispatch({
      type: ACTIONS.MARK_STEP_SKIPPED,
      payload: currentStepConfig.id,
    })

    if (currentStep < totalSteps - 1) {
      dispatch({ type: ACTIONS.SET_STEP, payload: currentStep + 1 })
    } else {
      handleSubmit()
    }
  }, [currentStep, totalSteps, currentStepConfig, handleSubmit])

  // Go back to previous step
  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      dispatch({ type: ACTIONS.SET_STEP, payload: currentStep - 1 })
    }
  }, [currentStep])

  // Calculate progress percentage
  const progressPercent = Math.round(((currentStep + 1) / totalSteps) * 100)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {t('wizard.title')}
            </h2>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label={t('common.close')}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress Bar */}
          <WizardProgress
            currentStep={currentStep + 1}
            totalSteps={totalSteps}
            percent={progressPercent}
            stepName={t(`wizard.steps.${currentStepConfig.id}.title`)}
          />
        </div>

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {StepComponent && (
            <StepComponent
              data={data[currentStepConfig.id]}
              onChange={(newData) => updateStepData(currentStepConfig.id, newData)}
            />
          )}
        </div>

        {/* Footer Navigation */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            {/* Back Button */}
            <button
              onClick={handleBack}
              disabled={currentStep === 0}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                currentStep === 0
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('wizard.back')}
            </button>

            {/* Right Side Buttons */}
            <div className="flex items-center space-x-3">
              {/* Skip Button */}
              <button
                onClick={handleSkip}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                {t('wizard.skip')}
              </button>

              {/* Next/Finish Button */}
              <button
                onClick={handleNext}
                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                {currentStep === totalSteps - 1 ? t('wizard.finish') : t('wizard.next')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Processing Modal */}
      {isProcessing && (
        <ProcessingModal
          error={processingError}
          onRetry={handleSubmit}
          onCancel={onCancel}
        />
      )}
    </div>
  )
}
